/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { useEffect } from 'react';

import {
	Box,
	Progress,
	Image,
	rem,
	Text,
} from '@mantine/core';
import classes from './SingleFileUploadWithProgress.module.css';

// Icons
import SheetIcon from '@assets/images/sheet-file-icon.svg';
import CancelUploadIcon from '@assets/images/cancel-upload-icon.svg';
import { IconCircleXFilled } from '@tabler/icons-react';

// Utils
import {
	bytesToMB,
	truncateString,
} from '@utils/functions';
import { Tooltip } from '@components/atoms/Tooltip';

import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import {
	MAX_SIZE_BYTES,
	ACCEPTED_MIME_TYPES,
} from './config';

export interface SingleFileUploadWithProgressProps {
	file: any;
	errors: any;
	onDelete: (file: File) => void;
	onUpload: (file: File, url: string) => void;
	onReload: () => void;
}

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const SingleFileUploadWithProgress = ({
	file,
	onDelete,
	errors,
}: SingleFileUploadWithProgressProps) => {
	const { t } = useTranslation();

	useEffect(() => {
		if (errors && errors[0] && errors[0].code) {
			const mimeValuesString =
				typeof ACCEPTED_MIME_TYPES === 'object' &&
				!Array.isArray(ACCEPTED_MIME_TYPES) &&
				ACCEPTED_MIME_TYPES !== null &&
				Object.values(ACCEPTED_MIME_TYPES)
					.flat()
					.join(', ');
			switch (errors[0].code) {
				case 'file-too-large':
					notifications.show({
						title: t('global.error'),
						message: `${t('errors.fileTooLarge')} ${bytesToMB(MAX_SIZE_BYTES)} ${t('global.megabyte')}`,
						color: 'error',
					});
					break;
				case 'duplicated-uploaded-files':
					notifications.show({
						title: t('global.error'),
						message: `${t('errors.duplicatedUploadedFiles')}`,
						color: 'error',
					});
					break;
				case 'file-invalid-type':
					notifications.show({
						title: t('global.error'),
						message: `${t('errors.fileTypeMustBe')} ${mimeValuesString}`,
						color: 'error',
					});
					break;
				default:
					notifications.show({
						title: t('global.error'),
						message: errors[0].message,
						color: 'error',
					});
					break;
			}
		}
	}, []);

	return (
		<Box className={classes.container}>
			<Box className={classes.wrapper}>
				<Image
					style={{
						width: rem(29),
						height: rem(29),
						color: 'var(--mantine-color-dimmed)',
					}}
					src={SheetIcon}
				/>
				<Tooltip
					isActive={
						file && file.path && file.path.length > 20
					}
					label={file.path}
				>
					<Text className={classes.filename}>
						{truncateString(file.path, 20)}
					</Text>
				</Tooltip>
				<Box className={classes.containerProgress}>
					<Progress.Root
						size="md"
						className={classes.dropzoneError}
					>
						<Progress.Section
							color="success"
							value={0}
							style={{
								borderRadius: '1px',
							}}
						/>
					</Progress.Root>

					<IconCircleXFilled
						style={{
							width: rem(30),
							height: rem(30),
							color: 'var(--mantine-color-error-9)',
						}}
						stroke={1.5}
					/>

					<Image
						data-testid="D2EtDXR4_Z5jdHi6B-button"
						onClick={() => {
							onDelete(file);
						}}
						className={classes.cancelUpload}
						src={CancelUploadIcon}
					/>
				</Box>
			</Box>
		</Box>
	);
};

export default SingleFileUploadWithProgress;
